import React, { FC, ReactNode } from 'react'
import cx from 'classnames'
import { get } from 'lodash-es'
import { isEmpty } from 'ramda'
import { useHover } from 'react-use'
import Switch from '../../../../atoms/Switch/Switch'
import Tooltip from '../../../../atoms/Tooltip/Tooltip'
import CurrencyField from '../../../../molecules/CurrencyField/CurrencyField'
import styles from '../Beneficiary.module.scss'
import SettledBeneficiary from '../SettledBeneficiary/SettledBeneficiary'
import NoBankDetails from '../NoBankDetails/NoBankDetails'
import { getLastFour, TPartialBankDetails } from './depositBeneficiaryUtils'
import { $TSFixMe } from 'types/ts-migrate'

type DepositBeneficiaryProps = {
  placeholder: string
  amountName?: string
  value: number | string
  index: number
  reference: string | ReactNode
  transfer: boolean
  errors?: string
  vatApplied: boolean
  disableTransferToggle?: boolean
  disableCurrencyField?: boolean
  shouldDisplayValueAsZero?: boolean
  isBeneficiarySettled: boolean
  amountPayable: number
  inputTooltipText?: string
  isEditing?: boolean
  managedByValue?: 'owner' | 'agency'
  invoiceStatus?: string | null
  propertyOwnerId?: string | null
  propertyOwnerName?: string | null
  propertyOwnerBankDetails?: any
  Meta?: FC
  includeVatCalculator?: boolean
  onNewBeneficiaryAmountChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onTransferChange?: (index: number, transfer: boolean) => void
  onVatChange?: (e: $TSFixMe) => void
}

const DepositBeneficiary: FC<DepositBeneficiaryProps> = props => {
  const {
    placeholder,
    amountName,
    value,
    index,
    reference,
    transfer,
    isEditing = false,
    vatApplied,
    includeVatCalculator = true,
    Meta,
    errors,
    disableTransferToggle = false,
    disableCurrencyField = true,
    shouldDisplayValueAsZero = false,
    isBeneficiarySettled,
    amountPayable,
    inputTooltipText,
    managedByValue = 'owner',
    invoiceStatus,
    propertyOwnerId,
    propertyOwnerName,
    propertyOwnerBankDetails,
    onNewBeneficiaryAmountChange,
    onTransferChange,
    onVatChange,
  } = props

  const classes = cx(styles.root, {
    [styles['is-editing']]: isEditing,
  })

  const managedByDescription = () => {
    const isManagedByOwner = !transfer && typeof propertyOwnerId !== 'undefined'

    return isManagedByOwner &&
      !isEmpty(propertyOwnerBankDetails?.bank) &&
      !isEmpty(propertyOwnerBankDetails?.accountNumber) ? (
      <span>
        <strong>{propertyOwnerName}</strong>
        {invoiceStatus !== 'Done' && (
          <>
            <br />
            {propertyOwnerBankDetails?.bank} *****
            {getLastFour(propertyOwnerBankDetails as TPartialBankDetails)}
          </>
        )}
      </span>
    ) : isManagedByOwner ? (
      <NoBankDetails partyId={propertyOwnerId} />
    ) : (
      <></>
    )
  }

  const [details, isHovering] = useHover(
    <div className={styles.details}>
      <div>
        {disableTransferToggle ? (
          <span>{transfer ? <strong>Managed by reOS</strong> : 'Self-managed by Owner'}</span>
        ) : (
          <>
            <Switch
              className={styles['transfer-toggle']}
              label={transfer ? 'Managed by reOS' : 'Self-managed by Owner'}
              checked={transfer}
              onChange={() => onTransferChange?.(index, !transfer)}
            />
          </>
        )}
      </div>
      <div>{!transfer && managedByDescription()}</div>
      <span className={styles.reference}>{reference}</span>
    </div>,
  )

  return (
    <li className={classes}>
      <Tooltip
        isOpen={!disableTransferToggle && isHovering}
        body={
          transfer
            ? 'The deposit amount is transferred to a reOS managed tenant deposit wallet.'
            : managedByValue === 'agency'
            ? 'The deposit amount is transferred to your external agency account.'
            : "The deposit amount is transferred to owner's account."
        }
      >
        {details}
      </Tooltip>
      {isBeneficiarySettled ? (
        <SettledBeneficiary amountPayable={amountPayable} />
      ) : (
        <div className={styles['input-wrapper']}>
          <CurrencyField
            disabled={disableCurrencyField}
            includeVatControl
            includeVatCalculator={includeVatCalculator}
            name={amountName}
            placeholder={placeholder}
            value={shouldDisplayValueAsZero ? '' : value}
            onChange={onNewBeneficiaryAmountChange}
            error={get(errors, 'amount')}
            onToggleChange={onVatChange}
            vatApplied={vatApplied}
            isEditVatDisabled={true}
            inputTooltipText={inputTooltipText}
          />
          {Meta && (
            <div className={styles.meta}>
              <Meta />
            </div>
          )}
        </div>
      )}
    </li>
  )
}

export default DepositBeneficiary
